/**
 * OTT TV activation: guest login redirect + 6-digit code form + activate API.
 */

const GUEST_REDIRECT_SELECTOR = '[data-ott-tv-guest-redirect]';
const ACTIVATION_TAG = 'ott-tv-activation';
const ACTIVATION_CODE_STORAGE_KEY = 'ott_tv_activation_code';

/**
 * @param {string} loginPath
 */
function redirectGuestToLogin(loginPath) {
  const basePath =
    loginPath ||
    (typeof Theme !== 'undefined' && Theme?.routes?.storefront_login_url) ||
    (typeof Theme !== 'undefined' && Theme?.routes?.account_login_url) ||
    '/customer_authentication/login';

  const url = new URL(
    basePath.startsWith('http') ? basePath : basePath,
    window.location.origin
  );
  const returnPath = window.location.pathname + window.location.search + window.location.hash || '/';
  url.searchParams.set('return_to', returnPath);
  window.location.replace(url.toString());
}

/**
 * @param {HTMLElement} host
 */
function initGuestRedirect(host) {
  const loginPath = host.dataset.loginUrl || '';
  redirectGuestToLogin(loginPath);
}

class OttTvActivation extends HTMLElement {
  /** @type {HTMLInputElement[]} */
  #digits = [];

  /** @type {HTMLButtonElement | null} */
  #continueBtn = null;

  /** @type {HTMLElement | null} */
  #errorEl = null;

  #isSubmitting = false;

  connectedCallback() {
    this.#digits = Array.from(this.querySelectorAll('[data-ott-digit]'));
    this.#continueBtn = this.querySelector('[data-ott-continue]');
    this.#errorEl = this.querySelector('[data-ott-error]');

    if (!this.#digits.length || !this.#continueBtn) {
      return;
    }

    for (const input of this.#digits) {
      const slot = input.closest('[data-ott-digit-slot]');
      if (slot) {
        slot.classList.toggle('is-filled', Boolean(input.value));
      }
    }

    for (const input of this.#digits) {
      input.addEventListener('input', this.#handleInput);
      input.addEventListener('keydown', this.#handleKeydown);
      input.addEventListener('paste', this.#handlePaste);
    }

    this.#continueBtn.addEventListener('click', this.#handleContinue);
    this.#updateContinueState();
  }

  disconnectedCallback() {
    for (const input of this.#digits) {
      input.removeEventListener('input', this.#handleInput);
      input.removeEventListener('keydown', this.#handleKeydown);
      input.removeEventListener('paste', this.#handlePaste);
    }

    this.#continueBtn?.removeEventListener('click', this.#handleContinue);
  }

  /**
   * @param {HTMLInputElement} input
   * @param {'next' | 'prev'} direction
   * @returns {HTMLInputElement | null}
   */
  #getAdjacentDigitInput(input, direction) {
    const slot = input.closest('[data-ott-digit-slot]');
    if (!slot) {
      return null;
    }

    const adjacentSlot =
      direction === 'next' ? slot.nextElementSibling : slot.previousElementSibling;
    const adjacentInput = adjacentSlot?.querySelector('[data-ott-digit]');
    return adjacentInput instanceof HTMLInputElement ? adjacentInput : null;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #syncDigitSlotState(input) {
    const slot = input.closest('[data-ott-digit-slot]');
    if (slot) {
      slot.classList.toggle('is-filled', Boolean(input.value));
    }
  }

  #handleInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    const value = input.value.replace(/\D/g, '');
    input.value = value.slice(-1);
    this.#syncDigitSlotState(input);

    if (input.value) {
      const nextInput = this.#getAdjacentDigitInput(input, 'next');
      if (nextInput) {
        nextInput.focus();
      }
    }

    this.#clearError();
    this.#updateContinueState();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.currentTarget);
    const prevInput = this.#getAdjacentDigitInput(input, 'prev');
    const nextInput = this.#getAdjacentDigitInput(input, 'next');

    if (event.key === 'Backspace' && !input.value && prevInput) {
      prevInput.focus();
      return;
    }

    if (event.key === 'ArrowLeft' && prevInput) {
      event.preventDefault();
      prevInput.focus();
      return;
    }

    if (event.key === 'ArrowRight' && nextInput) {
      event.preventDefault();
      nextInput.focus();
    }
  };

  /**
   * @param {ClipboardEvent} event
   */
  #handlePaste = (event) => {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!pasted) {
      return;
    }

    for (let i = 0; i < this.#digits.length; i++) {
      const digit = this.#digits[i];
      if (!digit) {
        continue;
      }

      digit.value = pasted[i] || '';
      this.#syncDigitSlotState(digit);
    }

    const nextEmpty = this.#digits.find((digit) => digit && !digit.value);
    const lastDigit = this.#digits[this.#digits.length - 1];
    (nextEmpty || lastDigit)?.focus();
    this.#clearError();
    this.#updateContinueState();
  };

  #getActivationCode() {
    return this.#digits.map((digit) => digit.value).join('');
  }

  #updateContinueState() {
    if (!this.#continueBtn) {
      return;
    }

    const expectedLength = Number(this.dataset.codeDigits) || this.#digits.length;
    const code = this.#getActivationCode();
    this.#continueBtn.disabled = this.#isSubmitting || code.length !== expectedLength;
  }

  #clearError() {
    if (!this.#errorEl) {
      return;
    }

    this.#errorEl.textContent = '';
    this.#errorEl.hidden = true;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    if (!this.#errorEl) {
      return;
    }

    this.#errorEl.textContent = message;
    this.#errorEl.hidden = false;
  }

  /**
   * @param {string} activationCode
   */
  #storeActivationCode(activationCode) {
    try {
      sessionStorage.setItem(ACTIVATION_CODE_STORAGE_KEY, activationCode);
    } catch {
      /* sessionStorage unavailable */
    }
  }

  /**
   * @param {string} successUrl
   */
  #redirectToSuccess(successUrl) {
    window.location.assign(successUrl);
  }

  /**
   * @param {unknown} payload
   * @returns {{ isPaired: boolean, errorMessage: string }}
   */
  #parseActivateResponse(payload) {
    const dataObj =
      payload &&
      typeof payload === 'object' &&
      payload !== null &&
      /** @type {Record<string, unknown>} */ (payload).data &&
      typeof /** @type {Record<string, unknown>} */ (payload).data === 'object'
        ? /** @type {{ response?: { dataObj?: { responseStatus?: string; responseErrorMsg?: string } } }} */ (
            payload
          ).data?.response?.dataObj
        : null;

    const responseStatus = dataObj?.responseStatus || '';
    const errorMessage = (dataObj?.responseErrorMsg || '').trim();

    return {
      isPaired: responseStatus === 'SUCCESS',
      errorMessage,
    };
  }

  #handleContinue = async () => {
    if (this.#isSubmitting || !this.#continueBtn) {
      return;
    }

    const expectedLength = Number(this.dataset.codeDigits) || this.#digits.length;
    const activationCode = this.#getActivationCode();

    if (activationCode.length !== expectedLength) {
      return;
    }

    const apiUrl = (this.dataset.apiUrl || '').trim();
    const amsCustomerCode = (this.dataset.amsCustomerCode || '').trim();
    const successUrl = (this.dataset.successUrl || '/pages/tv-preferences').trim();
    const genericError = this.dataset.errorGeneric || 'Something went wrong. Please try again.';
    const invalidError = this.dataset.errorInvalid || "Looks like we couldn't find the code below entered, Please recheck & try again.";
    const missingAmsError =
      this.dataset.errorMissingAms || 'Your account is not linked. Please contact customer support.';
    const skipApiValidation = this.dataset.skipApiValidation === 'true';

    if (skipApiValidation) {
      this.#storeActivationCode(activationCode);
      this.#redirectToSuccess(successUrl);
      return;
    }

    if (!apiUrl) {
      this.#showError(genericError);
      return;
    }

    if (!amsCustomerCode) {
      this.#showError(missingAmsError);
      return;
    }

    this.#isSubmitting = true;
    this.#continueBtn.disabled = true;
    const continueLabel = this.dataset.continueLabel || 'Continue';
    const submittingLabel = this.dataset.submittingLabel || continueLabel;
    this.#continueBtn.textContent = submittingLabel;
    this.#clearError();

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          activationCode,
          amsCustomerCode,
        }),
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      const { isPaired, errorMessage } = this.#parseActivateResponse(payload);

      if (response.ok && isPaired) {
        this.#storeActivationCode(activationCode);
        this.#redirectToSuccess(successUrl);
        return;
      }

      const payloadRecord =
        payload && typeof payload === 'object' && payload !== null
          ? /** @type {Record<string, unknown>} */ (payload)
          : null;

      const message =
        errorMessage ||
        (typeof payloadRecord?.message === 'string' ? payloadRecord.message : '') ||
        (typeof payloadRecord?.error === 'string' ? payloadRecord.error : '') ||
        (typeof payloadRecord?.error_message === 'string' ? payloadRecord.error_message : '') ||
        invalidError;
      this.#showError(message);
    } catch {
      this.#showError(genericError);
    } finally {
      this.#isSubmitting = false;
      this.#continueBtn.textContent = continueLabel;
      this.#updateContinueState();
    }
  };
}

function init() {
  const guestHost = document.querySelector(GUEST_REDIRECT_SELECTOR);
  if (guestHost) {
    initGuestRedirect(/** @type {HTMLElement} */ (guestHost));
    return;
  }

  if (!customElements.get(ACTIVATION_TAG)) {
    customElements.define(ACTIVATION_TAG, OttTvActivation);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
